import React, { useState, useMemo } from "react";
import { useTablePreferences } from "../hooks/useTablePreferences";
import { useListViews } from "../hooks/useListViews";
import { CustomButton } from "../components/ui/CustomButton";
import { User, ClinicUserApiResponse, ListView, ListViewData } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ManageColumnsModal, ColumnConfig } from "../components/modals/ManageColumnsModal";
import { SaveViewModal } from "../components/modals/SaveViewModal";
import { ViewSelector } from "../components/ui/ViewSelector";
import { ContextMenu } from "../components/ui/ContextMenu";
import { CreateUserModal } from "../components/modals/CreateUserModal";
import { EditUserModal } from "../components/modals/EditUserModal";
import apiService from "../services/api";
import { saveLastViewId, getLastViewId, clearLastViewId } from "../utils/viewPersistence";


interface UsersScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users", patientId?: string | number, meetingId?: string | number) => void;
    users: ClinicUserApiResponse[];
    onRefreshUsers?: () => Promise<void>;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

export const UsersScreen: React.FC<UsersScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    users: propsUsers,
    onRefreshUsers,
    taskCount,
    whatsappUnreadCount,
}) => {
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // Filter states
    const [activeFilter, setActiveFilter] = useState<"Active" | "Deactivated" | "All">("Active");
    const [typeFilter, setTypeFilter] = useState<string>("All");
    const [dataFilter, setDataFilter] = useState<string>("All");
    const [roleFilter, setRoleFilter] = useState<string>("All");

    // View management state
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);

    // Track the base view that was loaded, even after currentViewId is cleared
    // This allows users to save changes back to the original view
    const [baseViewId, setBaseViewId] = useState<number | null>(null);

    // Create user modal state
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

    // Edit user modal state
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<ClinicUserApiResponse | null>(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; user: ClinicUserApiResponse } | null>(null);

    // Check if user is admin
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    const token = localStorage.getItem("token") || "";

    // Define all available columns with their configurations
    const ALL_COLUMNS: ColumnConfig[] = [
        { id: "created_at", label: "Created", visible: true },
        { id: "updated_at", label: "Updated", visible: true },
        { id: "active", label: "Active", visible: true },
        { id: "name", label: "Name", visible: true },
        { id: "email", label: "Email", visible: true },
        { id: "phone", label: "Phone", visible: true },
        { id: "heb_name", label: "Hebrew Name", visible: true },
        { id: "update_in_clicks_user_id", label: "Update In Clicks", visible: false },
        { id: "password_set_on", label: "Password Set", visible: false },
        { id: "type", label: "Type", visible: true },
        { id: "see_all_patients", label: "See All Patients", visible: true },
        { id: "data", label: "Environment", visible: true },
        { id: "show_prep_meeting", label: "Prep Meeting", visible: false },
        { id: "update_summary_in_clicks", label: "Update Summary", visible: false },
        { id: "show_clicks_tasks", label: "Show Tasks", visible: false },
        { id: "manage_users", label: "Manage Users", visible: false },
        { id: "phone_ext", label: "Extension", visible: false },
        { id: "yoman_id", label: "Yoman ID", visible: false },
        { id: "last_login", label: "Last Login", visible: true },
        { id: "license", label: "License", visible: true },
        { id: "doc_id", label: "ID Number", visible: true },
        { id: "is_2FA_setup", label: "2FA Setup", visible: false },
    ];

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "name",
        sortOrder: "asc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'users-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    // List Views integration
    const {
        privateViews,
        publicViews,
        defaultView,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
        isCreating,
    } = useListViews({
        userId: user?.id as number,
        listName: 'users',
        token,
        enabled: !!user?.id && !!token,
    });

    // Track if we've already loaded the initial view to prevent re-loading when view is cleared
    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    React.useEffect(() => {
        // Only run after views are loaded
        if (isLoadingViews) {
            return;
        }

        // Only auto-load view once on initial mount
        if (hasLoadedInitialView.current) {
            return;
        }

        const savedViewId = getLastViewId('users');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                // View was deleted, clear the saved ID
                clearLastViewId('users');
            }
        } else if (defaultView) {
            // No saved view, use default view if one exists
            handleLoadView(defaultView);
            hasLoadedInitialView.current = true;
        } else {
            // No view to load, mark as loaded to prevent future auto-loads
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews, defaultView]);

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);

    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    // Clean up invalid columns on mount
    React.useEffect(() => {
        const validColumnIds = ALL_COLUMNS.map(col => col.id);
        const invalidColumns = columnOrder.filter(id => !validColumnIds.includes(id));

        if (invalidColumns.length > 0) {
            console.warn('Removing invalid columns:', invalidColumns);
            const cleanedColumnOrder = columnOrder.filter(id => validColumnIds.includes(id));
            setColumnOrder(cleanedColumnOrder);

            // Also clean up columnVisibility and columnWidths
            const cleanedVisibility = { ...columnVisibility };
            const cleanedWidths = { ...columnWidths };
            invalidColumns.forEach(id => {
                delete cleanedVisibility[id];
                delete cleanedWidths[id];
            });
            setColumnVisibility(cleanedVisibility);
            setColumnWidths(cleanedWidths);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only on mount

    // Sync preferences when state changes
    React.useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleSearchClick = () => {
        setSearch(searchInput.toLowerCase());

        // Check if we should clear the view (only if user can't save)
        clearViewOnChange();
    };

    // Helper function to check if user can save to the current view
    // Only clears currentViewId if user doesn't have permission to save (public view + not admin)
    const clearViewOnChange = React.useCallback(() => {
        if (currentViewId === null) return;

        // Find the current view to check its type
        const allViews = [...privateViews, ...publicViews];
        const currentView = allViews.find(v => v.id === currentViewId);

        if (!currentView) return;

        // Check if user can save to this view
        const isPrivateView = currentView.type === 'private';
        const isPublicView = currentView.type === 'public';
        const canSaveToPublicView = isAdmin;

        // User can save if:
        // 1. It's their private view, OR
        // 2. It's a public view AND they're an admin
        const canSaveToView = isPrivateView || (isPublicView && canSaveToPublicView);

        // Only clear view if user CAN'T save to it
        if (!canSaveToView) {
            // User doesn't have permission to save, move to "Default View"
            setCurrentViewId(null);
            clearLastViewId('users');
            // Note: baseViewId is kept so we remember where we came from
        }
        // If user CAN save, keep currentViewId so they can save their changes
    }, [currentViewId, privateViews, publicViews, isAdmin]);

    // Transformation functions
    const formatDate = (timestamp: number | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit' });
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            // Toggle sort order if clicking the same column
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            // Set new column and default to ascending
            setSortColumn(column);
            setSortOrder("asc");
        }

        // Check if we should clear the view (only if user can't save)
        clearViewOnChange();
    };

    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            if (resizingColumn) {
                // Check if we should clear the view (only if user can't save)
                clearViewOnChange();
            }
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth, currentViewId, privateViews, publicViews, isAdmin, clearViewOnChange]);

    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't sort if clicking on resize handle
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-gray-100 cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-blue-600">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                {/* Resize handle */}
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);

        // Check if we should clear the view (only if user can't save)
        clearViewOnChange();
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        // Also reset search and filters
        setSearch("");
        setSearchInput("");
        setActiveFilter("Active");
        setTypeFilter("All");
        setDataFilter("All");
        setRoleFilter("All");

        // Clear both view IDs
        setCurrentViewId(null);
        setBaseViewId(null);
        clearLastViewId('users');

        resetToDefaults();
    };

    // View management handlers
    const handleUpdateView = async () => {
        // Use baseViewId since currentViewId might be cleared after manual changes
        if (!baseViewId) return;

        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            filters: {
                search,
                activeFilter,
                typeFilter,
                dataFilter,
                roleFilter,
            },
        };

        try {
            await updateListView({
                id: baseViewId,
                data: viewData,
            });

            // After saving, restore currentViewId to baseViewId to reflect the saved state
            // This ensures the view selector shows the correct view name
            setCurrentViewId(baseViewId);
            saveLastViewId('users', baseViewId);

            // Mark as initially loaded to prevent auto-reload effects
            hasLoadedInitialView.current = true;

            // Could show a success toast here
        } catch (error) {
            console.error('Failed to update view:', error);
            // Could show an error toast here
        }
    };

    const handleSaveAsView = async (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => {
        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            description,
            filters: {
                search,
                activeFilter,
                typeFilter,
                dataFilter,
                roleFilter,
            },
            ...(isDefault && { is_default: true }),
        };

        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'users',
                data: viewData,
            });

            // Set both IDs to the newly created view
            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('users', newView.id);

            // Mark as initially loaded to prevent auto-reload
            hasLoadedInitialView.current = true;

            setIsSaveViewModalOpen(false);
            // Could show a success toast here
        } catch (error) {
            console.error('Failed to save view:', error);
            // Could show an error toast here
        }
    };

    const handleLoadView = (view: ListView) => {
        // Merge in any columns added since this view was saved so they still appear in Manage Columns.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter(id => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);

        // Apply filters from the view (or clear if not defined)
        if (view.data.filters) {
            setSearch(view.data.filters.search ?? "");
            setActiveFilter(view.data.filters.activeFilter ?? "Active");
            setTypeFilter(view.data.filters.typeFilter ?? "All");
            setDataFilter(view.data.filters.dataFilter ?? "All");
            setRoleFilter(view.data.filters.roleFilter ?? "All");
        } else {
            // Clear filters if the view doesn't have them defined
            setSearch("");
            setActiveFilter("Active");
            setTypeFilter("All");
            setDataFilter("All");
            setRoleFilter("All");
        }

        setCurrentViewId(view.id);
        setBaseViewId(view.id); // Track the base view for save functionality
        saveLastViewId('users', view.id);
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                // If we deleted the current view, reset to defaults
                handleResetColumns();
                setCurrentViewId(null);
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    // Filter and sort users data
    const filteredAndSortedUsers = useMemo(() => {
        let filtered = [...propsUsers];

        // Apply active filter
        if (activeFilter === "Active") {
            filtered = filtered.filter(u => u.active === true);
        } else if (activeFilter === "Deactivated") {
            filtered = filtered.filter(u => u.active === false);
        }
        // "All" doesn't filter

        // Apply type filter
        if (typeFilter !== "All") {
            filtered = filtered.filter(u => u.type === typeFilter);
        }

        // Apply data (environment) filter
        if (dataFilter !== "All") {
            filtered = filtered.filter(u => u.data === dataFilter);
        }

        // Apply role filter
        if (roleFilter !== "All") {
            filtered = filtered.filter(u => u.role === roleFilter);
        }

        // Apply search filter
        if (search) {
            filtered = filtered.filter(u =>
                (u.name?.toLowerCase() || "").includes(search) ||
                (u.email?.toLowerCase() || "").includes(search) ||
                (u.phone?.toLowerCase() || "").includes(search) ||
                (u.heb_name?.toLowerCase() || "").includes(search)
            );
        }

        // Apply sorting
        filtered.sort((a: any, b: any) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) aVal = "";
            if (bVal === null || bVal === undefined) bVal = "";

            // For booleans, convert to number for sorting
            if (typeof aVal === "boolean") aVal = aVal ? 1 : 0;
            if (typeof bVal === "boolean") bVal = bVal ? 1 : 0;

            // For strings, compare case-insensitively
            if (typeof aVal === "string" && typeof bVal === "string") {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
            if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [propsUsers, search, sortColumn, sortOrder, activeFilter, typeFilter, dataFilter, roleFilter]);

    return (
        <Layout
            userName={user.name || user.email}
            onLogout={onLogout}
            activeMenuItem="users"
            onNavigate={onNavigate}
            institutionId={user.institution_id}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            <div className="p-6">
                <div className="bg-white rounded-lg shadow-sm">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200">
                        {/* Header row with title and view selector */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
                                <ViewSelector
                                    privateViews={privateViews}
                                    publicViews={publicViews}
                                    currentViewId={currentViewId}
                                    baseViewId={baseViewId}
                                    onSelectView={handleLoadView}
                                    onSaveView={handleUpdateView}
                                    onSaveAsView={() => setIsSaveViewModalOpen(true)}
                                    onDeleteView={handleDeleteView}
                                    isLoading={isLoadingViews}
                                    isAdmin={isAdmin}
                                />
                            </div>
                        </div>

                        {/* Search and Controls */}
                        <div className="flex items-center gap-4 flex-wrap">
                            {/* Search */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchInput}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchClick();
                                        }
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    style={{ width: 250 }}
                                />
                                <button
                                    onClick={handleSearchClick}
                                    className="px-4 py-2 rounded-md text-gray-700 font-medium transition-all duration-200 hover:shadow-lg hover:bg-opacity-20"
                                    style={{
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(209, 213, 219, 0.3)',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                        background: 'rgba(249, 250, 251, 0.6)',
                                    }}
                                    title="Search"
                                >
                                    🔍
                                </button>
                            </div>

                            {/* Filters */}
                            <div className="flex items-center gap-2">
                                {/* Active filter */}
                                <select
                                    value={activeFilter}
                                    onChange={(e) => {
                                        setActiveFilter(e.target.value as "Active" | "Deactivated" | "All");
                                        clearViewOnChange();
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Deactivated">Deactivated</option>
                                    <option value="All">All Status</option>
                                </select>

                                {/* Type filter */}
                                <select
                                    value={typeFilter}
                                    onChange={(e) => {
                                        setTypeFilter(e.target.value);
                                        clearViewOnChange();
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="All">All Types</option>
                                    {Array.from(new Set(propsUsers.map(u => u.type).filter(Boolean))).sort().map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>

                                {/* Data (Environment) filter */}
                                <select
                                    value={dataFilter}
                                    onChange={(e) => {
                                        setDataFilter(e.target.value);
                                        clearViewOnChange();
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                    <option value="All">All Environments</option>
                                    {Array.from(new Set(propsUsers.map(u => u.data).filter(Boolean))).sort().map(data => (
                                        <option key={data} value={data}>{data}</option>
                                    ))}
                                </select>


                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center gap-2 ml-auto">
                                <CustomButton
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setIsCreateUserModalOpen(true)}
                                >
                                    + New User
                                </CustomButton>
                                <CustomButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleManageColumns}
                                >
                                    Manage Columns
                                </CustomButton>
                                <CustomButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleResetColumns}
                                >
                                    Reset
                                </CustomButton>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        {filteredAndSortedUsers.length === 0 ? (
                            <div className="text-center py-12 text-gray-600">
                                <p>No users found</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                            const columnConfig: Record<string, { label: string; nonSortable?: boolean }> = {
                                                "created_at": { label: "Created" },
                                                "updated_at": { label: "Updated" },
                                                "active": { label: "Active", nonSortable: true },
                                                "name": { label: "Name" },
                                                "email": { label: "Email" },
                                                "phone": { label: "Phone" },
                                                "heb_name": { label: "Hebrew Name" },
                                                "update_in_clicks_user_id": { label: "Update In Clicks" },
                                                "password_set_on": { label: "Password Set" },
                                                "type": { label: "Type" },
                                                "see_all_patients": { label: "See All Patients", nonSortable: true },
                                                "data": { label: "Environment" },
                                                "show_prep_meeting": { label: "Prep Meeting", nonSortable: true },
                                                "update_summary_in_clicks": { label: "Update Summary", nonSortable: true },
                                                "show_clicks_tasks": { label: "Show Tasks", nonSortable: true },
                                                "manage_users": { label: "Manage Users", nonSortable: true },
                                                "phone_ext": { label: "Extension" },
                                                "yoman_id": { label: "Yoman ID" },
                                                "last_login": { label: "Last Login" },
                                                "license": { label: "License" },
                                                "doc_id": { label: "ID Number" },
                                                "is_2FA_setup": { label: "2FA Setup", nonSortable: true }
                                            };

                                            const config = columnConfig[columnId];
                                            // Skip columns that don't exist in columnConfig
                                            if (!config) {
                                                console.warn(`Column ${columnId} not found in columnConfig`);
                                                return null;
                                            }
                                            return (
                                                <ResizableHeader
                                                    key={columnId}
                                                    column={columnId}
                                                    label={config.label}
                                                    nonSortable={config.nonSortable}
                                                />
                                            );
                                        }).filter(Boolean)}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredAndSortedUsers.map((userItem: any) => {
                                        const getCellContent = (columnId: string) => {
                                            switch (columnId) {
                                                case "created_at":
                                                    return formatDate(userItem.created_at) || "-";
                                                case "updated_at":
                                                    return formatDate(userItem.updated_at) || "-";
                                                case "active":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.active ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                case "name":
                                                    return userItem.name ? (
                                                        <button
                                                            data-ai-id="users-name-link"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedUserForEdit(userItem);
                                                                setIsEditUserModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                        >
                                                            {userItem.name}
                                                        </button>
                                                    ) : "-";
                                                case "email":
                                                    return userItem.email || "-";
                                                case "phone":
                                                    return userItem.phone || "-";
                                                case "heb_name":
                                                    return userItem.heb_name || "-";
                                                case "update_in_clicks_user_id":
                                                    if (!userItem.update_in_clicks_user_id) return "-";
                                                    const clicksUser = propsUsers.find(u => u.id === userItem.update_in_clicks_user_id);
                                                    return clicksUser?.name || userItem.update_in_clicks_user_id;
                                                case "password_set_on":
                                                    return formatDate(userItem.password_set_on) || "-";
                                                case "type":
                                                    return userItem.type || "-";
                                                case "see_all_patients":
                                                    const seeAllPatients = userItem.role === "Admin";
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${seeAllPatients ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {seeAllPatients ? "true" : "false"}
                                                        </span>
                                                    );
                                                case "data":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.data === 'prod' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                            {userItem.data || "-"}
                                                        </span>
                                                    );
                                                case "show_prep_meeting":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.show_prep_meeting ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.show_prep_meeting ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                case "update_summary_in_clicks":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.update_summary_in_clicks ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.update_summary_in_clicks ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                case "show_clicks_tasks":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.show_clicks_tasks ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.show_clicks_tasks ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                case "manage_users":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.manage_users ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.manage_users ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                case "phone_ext":
                                                    return userItem.phone_ext || "-";
                                                case "yoman_id":
                                                    return userItem.yoman_id || "-";
                                                case "last_login":
                                                    return formatDate(userItem.last_login) || "-";
                                                case "license":
                                                    return userItem.license || "-";
                                                case "doc_id":
                                                    return userItem.doc_id || "-";
                                                case "is_2FA_setup":
                                                    return (
                                                        <span className={`px-2 py-1 text-xs rounded ${userItem.is_2FA_setup ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                            {userItem.is_2FA_setup ? "Yes" : "No"}
                                                        </span>
                                                    );
                                                default:
                                                    return "-";
                                            }
                                        };

                                        return (
                                            <tr
                                                key={userItem.id}
                                                className="hover:bg-gray-50 cursor-pointer"
                                                onClick={(e) => {
                                                    // Don't open context menu if clicking on interactive elements
                                                    const target = e.target as HTMLElement;
                                                    if (
                                                        target.tagName === 'BUTTON' ||
                                                        target.tagName === 'A' ||
                                                        target.closest('button') ||
                                                        target.closest('a')
                                                    ) {
                                                        return;
                                                    }
                                                    setContextMenu({
                                                        x: e.clientX,
                                                        y: e.clientY,
                                                        user: userItem
                                                    });
                                                }}
                                            >
                                                {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                                    const width = columnWidths[columnId];
                                                    return (
                                                        <td
                                                            key={columnId}
                                                            className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                                                            style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
                                                        >
                                                            {getCellContent(columnId)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Results count */}
                    <div className="px-6 py-4 border-t border-gray-200 text-sm text-gray-600">
                        Showing {filteredAndSortedUsers.length} of {propsUsers.length} users
                    </div>
                </div>
            </div>

            {/* Manage Columns Modal */}
            {isManageColumnsOpen && (
                <ManageColumnsModal
                    isOpen={isManageColumnsOpen}
                    onClose={() => setIsManageColumnsOpen(false)}
                    columns={columnOrder
                        .map(id => {
                            const column = ALL_COLUMNS.find(c => c.id === id);
                            if (!column) return null; // Skip columns that don't exist in ALL_COLUMNS
                            return {
                                id,
                                label: column.label,
                                visible: columnVisibility[id]
                            };
                        })
                        .filter((col): col is { id: string; label: string; visible: boolean } => col !== null)}
                    onSave={handleSaveColumns}
                />
            )}

            {/* Save As View Modal */}
            <SaveViewModal
                isOpen={isSaveViewModalOpen}
                onClose={() => setIsSaveViewModalOpen(false)}
                onSave={handleSaveAsView}
                currentConfig={{
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                }}
                isAdmin={isAdmin}
                isSaving={isCreating}
                existingDefaultView={defaultView}
            />

            {/* Create User Modal */}
            <CreateUserModal
                isOpen={isCreateUserModalOpen}
                onClose={() => setIsCreateUserModalOpen(false)}
                onSuccess={async () => {
                    // Refresh users list
                    if (onRefreshUsers) {
                        await onRefreshUsers();
                    }
                }}
                token={token}
            />

            {/* Edit User Modal */}
            <EditUserModal
                isOpen={isEditUserModalOpen}
                onClose={() => {
                    setIsEditUserModalOpen(false);
                    setSelectedUserForEdit(null);
                }}
                onSuccess={async () => {
                    // Refresh users list
                    if (onRefreshUsers) {
                        await onRefreshUsers();
                    }
                }}
                token={token}
                user={selectedUserForEdit}
            />

            {/* Context Menu */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    options={[
                        {
                            label: 'Edit',
                            icon: '✏️',
                            onClick: () => {
                                setSelectedUserForEdit(contextMenu.user);
                                setIsEditUserModalOpen(true);
                                setContextMenu(null);
                            }
                        },
                        ...(contextMenu.user.active ? [
                            {
                                label: 'Deactivate',
                                icon: '🔴',
                                onClick: async () => {
                                    const selectedUser = contextMenu.user;
                                    setContextMenu(null);
                                    try {
                                        await apiService.activateUser(token, selectedUser.id, false);
                                        // Refresh users list
                                        if (onRefreshUsers) {
                                            await onRefreshUsers();
                                        }
                                    } catch (error) {
                                        console.error('Failed to deactivate user:', error);
                                        alert('Failed to deactivate user');
                                    }
                                }
                            }
                        ] : [
                            {
                                label: 'Activate',
                                icon: '🟢',
                                onClick: async () => {
                                    const selectedUser = contextMenu.user;
                                    setContextMenu(null);
                                    try {
                                        await apiService.activateUser(token, selectedUser.id, true);
                                        // Refresh users list
                                        if (onRefreshUsers) {
                                            await onRefreshUsers();
                                        }
                                    } catch (error) {
                                        console.error('Failed to activate user:', error);
                                        alert('Failed to activate user');
                                    }
                                }
                            }
                        ])
                    ]}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </Layout>
    );
};
